import React from 'react';
import { Skeleton } from './skeleton';
import { cn } from '@/lib/utils';

/**
 * TableSkeleton - Loading state for table views
 * @param {number} rows - Number of skeleton rows to display
 * @param {number} columns - Number of skeleton columns to display
 */
export const TableSkeleton = ({ rows = 5, columns = 6, className }) => {
  return (
    <div className={cn("w-full", className)} data-testid="table-skeleton">
      {/* Header */}
      <div className="flex gap-4 p-4 border-b bg-muted/30">
        {[...Array(columns)].map((_, i) => (
          <Skeleton key={`header-${i}`} className="h-4 flex-1" />
        ))}
      </div>
      {/* Rows */}
      {[...Array(rows)].map((_, rowIndex) => (
        <div key={`row-${rowIndex}`} className="flex gap-4 p-4 border-b">
          {[...Array(columns)].map((_, colIndex) => (
            <Skeleton 
              key={`cell-${rowIndex}-${colIndex}`} 
              className={cn(
                "h-4 flex-1",
                colIndex === 0 && "max-w-[200px]",
                colIndex === columns - 1 && "max-w-[100px]"
              )} 
            />
          ))}
        </div>
      ))}
    </div>
  );
};

/**
 * KanbanSkeleton - Loading state for kanban board views
 * @param {number} columns - Number of skeleton columns to display
 * @param {number} cardsPerColumn - Number of card skeletons per column
 */
export const KanbanSkeleton = ({ columns = 5, cardsPerColumn = 3, className }) => {
  return (
    <div className={cn("flex gap-4 overflow-x-auto pb-4", className)} data-testid="kanban-skeleton">
      {[...Array(columns)].map((_, colIndex) => (
        <div key={`col-${colIndex}`} className="flex-shrink-0 w-80 space-y-2">
          {/* Column Header */}
          <div className="p-4 rounded-lg bg-card border">
            <div className="flex items-center justify-between mb-2">
              <Skeleton className="h-6 w-24" />
              <Skeleton className="h-5 w-8 rounded-full" />
            </div>
            <Skeleton className="h-4 w-20" />
          </div>
          {/* Cards */}
          <div className="space-y-2 min-h-[400px] p-2 rounded-lg bg-muted/20">
            {[...Array(cardsPerColumn)].map((_, cardIndex) => (
              <div key={`card-${colIndex}-${cardIndex}`} className="p-4 rounded-lg bg-card border space-y-3">
                <div className="flex justify-between items-start">
                  <Skeleton className="h-4 w-32" />
                  <div className="flex gap-1">
                    <Skeleton className="h-6 w-6 rounded" />
                    <Skeleton className="h-6 w-6 rounded" />
                  </div>
                </div>
                <Skeleton className="h-3 w-24" />
                <Skeleton className="h-4 w-16" />
                <div className="flex gap-2 pt-2 border-t">
                  <Skeleton className="h-7 w-7" />
                  <Skeleton className="h-7 w-7" />
                  <Skeleton className="h-7 w-7" />
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

/**
 * CardGridSkeleton - Loading state for card grid views
 * @param {number} count - Number of skeleton cards to display
 */
export const CardGridSkeleton = ({ count = 6, className }) => {
  return (
    <div className={cn("grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4", className)} data-testid="card-grid-skeleton">
      {[...Array(count)].map((_, i) => (
        <div key={`card-${i}`} className="p-6 rounded-lg bg-card border space-y-4">
          <div className="flex justify-between items-start">
            <Skeleton className="h-5 w-32" />
            <Skeleton className="h-5 w-16 rounded-full" />
          </div>
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
          <div className="flex gap-2 pt-2">
            <Skeleton className="h-8 w-20" />
            <Skeleton className="h-8 w-20" />
          </div>
        </div>
      ))}
    </div>
  );
};

/**
 * ListSkeleton - Loading state for simple list views
 * @param {number} items - Number of skeleton items to display
 */
export const ListSkeleton = ({ items = 5, className }) => {
  return (
    <div className={cn("space-y-3", className)} data-testid="list-skeleton">
      {[...Array(items)].map((_, i) => (
        <div key={`item-${i}`} className="flex items-center gap-4 p-4 rounded-lg border">
          <Skeleton className="h-10 w-10 rounded-full" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-1/3" />
            <Skeleton className="h-3 w-1/2" />
          </div>
          <Skeleton className="h-8 w-20" />
        </div>
      ))}
    </div>
  );
};

/**
 * StatsSkeleton - Loading state for stats/dashboard cards
 * @param {number} count - Number of stat cards to display
 */
export const StatsSkeleton = ({ count = 4, className }) => {
  return (
    <div className={cn("grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4", className)} data-testid="stats-skeleton">
      {[...Array(count)].map((_, i) => (
        <div key={`stat-${i}`} className="p-6 rounded-lg bg-card border space-y-2">
          <div className="flex justify-between items-center">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-8 w-8 rounded" />
          </div>
          <Skeleton className="h-8 w-20" />
          <Skeleton className="h-3 w-16" />
        </div>
      ))}
    </div>
  );
};

export default {
  TableSkeleton,
  KanbanSkeleton,
  CardGridSkeleton,
  ListSkeleton,
  StatsSkeleton
};
